import { IS, IS_NOT_LIKE_NULL, NO, UIView, YES } from "uicore-ts"
import { RHFocusManager } from "./RHFocusManager"
import { RHNativeFocusItem } from "./RHNativeFocusItem"
import "./RHFocusManagerExtensions"


/**
 * Returns true if the given element accepts free-text keyboard input.
 *
 * Used by the Tab interception logic to decide whether a Tab keypress should
 * be consumed by the focus manager or left to the browser (e.g. when a text
 * field is active and the user is typing inside it, Tab should still navigate).
 */
export function elementAcceptsTyping(element: Element | undefined | null) {
    if (!element) {
        return false
    }
    
    if (element instanceof HTMLTextAreaElement) {
        return !element.readOnly && !element.disabled
    }
    
    // Number inputs are intentionally excluded — Tab navigation inside them is ambiguous.
    if (element instanceof HTMLInputElement) {
        const textTypes = new Set(["text", "search", "url", "tel", "password", "email"])
        return textTypes.has(element.type) && !element.readOnly && !element.disabled
    }
    
    return element instanceof HTMLElement && element.isContentEditable
}


/**
 * Document-level Tab / Shift+Tab focus manager for the application.
 *
 * Intercepts Tab keypresses globally (installed in RootViewController) and
 * delegates navigation to the set of `RHFocusManager` instances registered on
 * the active layer.
 *
 * ### Layer model
 * Focus is organised into layers so that modal surfaces (dialogs, overlays) can
 * take exclusive control of Tab navigation without disturbing the base layer.
 *
 * - **Base layer** (`_layers[0]`): always present; holds the managers for the
 *   main chrome (e.g. TopBar) and the currently visible content view controller.
 * - **Pushed layers** (`_layers[1..n]`): each `pushLayer` call adds a new
 *   single-manager layer on top. Tab is constrained to the topmost layer until
 *   `popLayer` removes it.
 *
 * ### Nested managers
 * Each `RHFocusManager`'s `focusableItems` callback may return a mix of
 * `UIView` and `RHFocusManager` instances. Nested managers are recursively
 * expanded depth-first by `RHFocusManager._expandItems` before navigation is
 * resolved, so sub-regions can delegate their own focusable-item logic without
 * the parent layer needing to know about their internal structure.
 *
 * ### Usage
 * 1. Call `addToBaseLayer` once for persistent chrome (TopBar).
 * 2. Call `register` / `deregister` in `viewWillAppear` / `viewWillDisappear`
 *    for content view controllers.
 * 3. Call `pushLayer` / `popLayer` when opening / closing modal surfaces.
 * 4. Install `handleTabEvent` as the document-level Tab handler in RootViewController.
 */
export class RHPageFocusManager {
    
    static readonly shared = new RHPageFocusManager()
    
    /**
     * Stack of layers. Index 0 is the base layer; higher indices are pushed
     * modal layers. Each entry is an ordered list of `RHFocusManager` instances
     * whose items are visited in sequence by Tab.
     */
    _layers: RHFocusManager[][] = [[]]
    
    _topBarManager?: RHFocusManager
    _bottomBarManager?: RHFocusManager
    
    /** The layer that currently receives Tab navigation. */
    get _activeLayer(): RHFocusManager[] {
        if (this._layers.length > 1) {
            return this._layers.lastElement
        }
        return [this._topBarManager, ...this._layers.firstElement, this._bottomBarManager]
            .filter(object => IS(object))
    }
    
    get _activeLayerContentManagers(): RHFocusManager[] {
        if (this._layers.length > 1) {
            return this._layers.lastElement
        }
        return this._layers.firstElement.filter(object => IS(object))
    }
    
    /**
     * Prepends `manager` to the base layer, giving it the highest Tab priority.
     * No-ops if already registered. Intended for persistent chrome (e.g. TopBar).
     */
    registerTopBarManager(manager: RHFocusManager) {
        this._topBarManager = manager
    }
    
    /**
     * Prepends `manager` to the base layer, giving it the highest Tab priority.
     * No-ops if already registered. Intended for persistent chrome (e.g. TopBar).
     */
    registerBottomBarManager(manager: RHFocusManager) {
        this._bottomBarManager = manager
    }
    
    
    /**
     * Registers `manager` as the active content manager in the base layer,
     * replacing the previous content manager if one exists. The first item is
     * focused by default; pages that should preserve neutral entry focus may
     * pass `focusesFirstItem: NO`. Call in `viewWillAppear`.
     */
    registerContentControllerManager(
        manager: RHFocusManager,
        options: { focusesFirstItem?: boolean } = {}
    ) {
        const layer = this._layers.firstElement
        if (!layer.contains(manager)) {
            layer.push(manager)
        }

        if (options.focusesFirstItem !== NO) {
            this._focusItem(this._activeLayerContentManagers.firstElement._activeItems().firstElement)
        }
    }
    
    /** Removes `manager` from the base layer. Call in `viewWillDisappear`. */
    deregisterContentControllerManager(manager: RHFocusManager) {
        this._layers.firstElement.removeElement(manager)
    }
    
    
    /** Pushes a new layer onto the stack, confining Tab navigation to `manager`. Call when opening a modal. */
    pushManagerAsNewLayer(manager: RHFocusManager) {
        this._layers.push([manager])
    }
    
    /** Removes the topmost layer, restoring Tab navigation to the layer below. Call when closing a modal. */
    popLayer() {
        if (this._layers.length > 1) {
            this._layers.pop()
        }
    }
    
    
    /**
     * Ensures a `tabindex` attribute is present on the item's element before
     * focusing it — required for non-interactive elements to receive a focus ring.
     */
    private _focusItem(item: UIView | RHNativeFocusItem) {
        if (IS_NOT_LIKE_NULL(item) && typeof item.focus === "function") {
            if (item.viewHTMLElement && !item.viewHTMLElement.hasAttribute("tabindex")) {
                item.viewHTMLElement.setAttribute("tabindex", "0")
            }
            item.focus()
        }
    }
    
    
    /**
     * Handles a Tab or Shift+Tab keypress. Collects all focusable items from
     * the active layer into a flat list (recursively expanding any nested
     * `RHFocusManager` instances), finds the currently focused one, and
     * steps forward or backward with wrapping.
     *
     * Returns `YES` if consumed, `NO` if there were no items to navigate.
     */
    handleTabEvent(event: KeyboardEvent): boolean {
        const didNavigate = this._stepFocus(event.shiftKey, () => event.preventDefault())
        return didNavigate ? YES : NO
    }
    
    
    /**
     * Moves focus to the next focusable item in the active layer, wrapping
     * to the first item if focus is currently on the last one (or outside
     * all known items). Intended for programmatic navigation triggered by
     * something other than an actual Tab keypress — e.g. an Enter handler
     * that should advance focus the same way Tab would.
     *
     * Returns `YES` if focus moved, `NO` if there were no items to navigate.
     */
    focusNext(): boolean {
        return this._stepFocus(NO)
    }
    
    
    /**
     * Moves focus to the previous focusable item in the active layer,
     * mirroring `focusNext()` but stepping backward (as Shift+Tab would).
     *
     * Returns `YES` if focus moved, `NO` if there were no items to navigate.
     */
    focusPrevious(): boolean {
        return this._stepFocus(YES)
    }
    
    
    /**
     * Shared stepping logic used by both `handleTabEvent` (a real Tab
     * keypress) and the programmatic `focusNext` / `focusPrevious` entry
     * points.
     *
     * `preventDefault` is an optional hook invoked whenever a real keyboard
     * event needs to be suppressed — `handleTabEvent` passes the event's
     * `preventDefault`, while the programmatic entry points omit it since
     * there is no event to suppress.
     */
    _stepFocus(isBackward: boolean, preventDefault?: () => void): boolean {
        const items = this._activeLayer.flatMap(manager =>
            RHFocusManager._expandItems(manager._activeItems())
        )
        
        if (!items.length) {
            return NO
        }
        
        // Find the current item index. First try the UIView walk (existing path),
        // then fall back to checking whether activeElement is inside any
        // RHNativeFocusItem's element (for plain DOM elements in the calendar etc.).
        let currentIndex = -1
        const activeElement = document.activeElement
        if (activeElement) {
            let element = activeElement as HTMLElement | null
            while (element) {
                const uiViewObject: UIView | undefined = element.UIViewObject
                if (!uiViewObject) {
                    element = element.parentElement
                    continue
                }
                const itemIndex = items.indexOf(uiViewObject)
                if (itemIndex >= 0) {
                    currentIndex = itemIndex
                    break
                }
                element = element.parentElement
            }
        }
        
        // Native-element fallback: check if activeElement is contained within
        // any RHNativeFocusItem's element.
        if (currentIndex < 0 && activeElement) {
            currentIndex = items.findIndex(
                item => item instanceof RHNativeFocusItem && item.element.contains(activeElement)
            )
        }
        
        // If no item was found, then just pick the last item of the first manager if positive direction
        // and first item of the second if negative direction
        if (currentIndex < 0) {
            if (!isBackward) {
                this._focusItem(
                    this._activeLayerContentManagers.flatMap(
                        manager => manager._activeItems()
                    ).firstElement
                )
            }
            else {
                const itemBeforeFirstContentItem = (
                    this._topBarManager ??
                    this._bottomBarManager ??
                    this._activeLayerContentManagers.lastElement
                )._activeItems().lastElement
                if (!itemBeforeFirstContentItem) {
                    return NO
                }
                preventDefault?.()
                this._focusItem(
                    itemBeforeFirstContentItem
                )
            }
            return YES
        }
        
        // currentIndex is -1 when focus is outside all known items — adding items.length
        // before the modulo ensures the arithmetic wraps correctly in both directions.
        const delta = isBackward ? -1 : 1
        const itemToFocus = items[(currentIndex + delta + items.length) % items.length]
        if (itemToFocus) {
            preventDefault?.()
            this._focusItem(itemToFocus)
            return YES
        }
        return NO
    }
    
}
